import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useUpdateSetting } from '@/hooks/useSetting';

const MAX_HEADERS = 20;
const MAX_VALUE_LENGTH = 4096;

const DENIED_HEADER_NAMES = new Set([
  'host',
  'content-length',
  'transfer-encoding',
  'chariot',
  'user-agent',
  'connection',
  'upgrade',
  'authorization',
]);

/**
 * RFC 7230 token characters: letters, digits, !#$%&'*+-.^_`|~
 * Matches backend isRFC7230Token in setting-validators.go:264-277
 */
const RFC_7230_TOKEN_RE = /^[A-Za-z0-9!#$%&'*+\-.^_`|~]+$/;

/**
 * Visible ASCII + SP + HTAB only (0x09, 0x20-0x7E)
 * Matches backend customHeadersValidator byte check
 */
function isValidHeaderValue(val: string): boolean {
  for (let i = 0; i < val.length; i++) {
    const code = val.charCodeAt(i);
    if (!(code === 0x09 || (code >= 0x20 && code <= 0x7e))) {
      return false;
    }
  }
  return true;
}

const headerRowSchema = z.object({
  name: z
    .string()
    .min(1, 'Header name is required')
    .regex(RFC_7230_TOKEN_RE, 'Invalid characters in header name')
    .refine(
      val => !DENIED_HEADER_NAMES.has(val.toLowerCase()),
      'This header name is not allowed'
    ),
  value: z
    .string()
    .max(
      MAX_VALUE_LENGTH,
      `Value must not exceed ${MAX_VALUE_LENGTH} characters`
    )
    .refine(isValidHeaderValue, 'Value contains disallowed characters'),
});

const customHeadersSchema = z
  .array(headerRowSchema)
  .max(MAX_HEADERS, `Maximum ${MAX_HEADERS} headers allowed`);

type HeaderRow = z.infer<typeof headerRowSchema>;

interface CustomScanHeadersFormProps {
  customerEmail?: string;
  initialHeaders: Record<string, string>;
  onClose?: () => void;
}

export function CustomScanHeadersForm({
  customerEmail,
  initialHeaders,
  onClose,
}: CustomScanHeadersFormProps) {
  // Convert map to array of rows for editing
  const [rows, setRows] = useState<HeaderRow[]>(() => {
    const entries = Object.entries(initialHeaders);
    return entries.length > 0
      ? entries.map(([name, value]) => ({ name, value }))
      : [{ name: '', value: '' }];
  });

  const [errors, setErrors] = useState<
    Record<number, { name?: string; value?: string }>
  >({});
  const { mutateAsync: updateSetting, status: updateStatus } =
    useUpdateSetting(customerEmail);
  const isSaving = updateStatus === 'pending';

  const updateRow = (index: number, field: 'name' | 'value', val: string) => {
    setRows(prev =>
      prev.map((r, i) => (i === index ? { ...r, [field]: val } : r))
    );
    // Clear error for this field on change
    setErrors(prev => {
      const rowErrors = { ...prev[index] };
      delete rowErrors[field];
      return { ...prev, [index]: rowErrors };
    });
  };

  const addRow = () => {
    if (rows.length >= MAX_HEADERS) {
      toast.error(`Maximum ${MAX_HEADERS} headers allowed`);
      return;
    }
    setRows(prev => [...prev, { name: '', value: '' }]);
  };

  const removeRow = (index: number) => {
    setRows(prev => prev.filter((_, i) => i !== index));
    setErrors(prev => {
      const next: Record<number, { name?: string; value?: string }> = {};
      for (const [key, val] of Object.entries(prev)) {
        const k = Number(key);
        if (k < index) next[k] = val;
        else if (k > index) next[k - 1] = val;
      }
      return next;
    });
  };

  const validate = (): boolean => {
    // Filter out completely empty rows (they get skipped on save)
    const nonEmptyRows = rows.filter(r => r.name || r.value);
    const result = customHeadersSchema.safeParse(nonEmptyRows);
    if (result.success) {
      setErrors({});
      return true;
    }

    const newErrors: Record<number, { name?: string; value?: string }> = {};
    for (const issue of result.error.issues) {
      if (issue.path.length >= 2) {
        const rowIndex = issue.path[0] as number;
        // Map back to original row indices (accounting for skipped empty rows)
        const field = issue.path[1] as 'name' | 'value';
        // Find the original index of this non-empty row
        let originalIndex = -1;
        let nonEmptyCount = 0;
        for (let i = 0; i < rows.length; i++) {
          if (rows[i].name || rows[i].value) {
            if (nonEmptyCount === rowIndex) {
              originalIndex = i;
              break;
            }
            nonEmptyCount++;
          }
        }
        if (originalIndex >= 0) {
          if (!newErrors[originalIndex]) newErrors[originalIndex] = {};
          newErrors[originalIndex][field] = issue.message;
        }
      }
    }
    setErrors(newErrors);
    return false;
  };

  // Check for duplicate header names
  const checkDuplicates = (): boolean => {
    const seen = new Set<string>();
    const newErrors: Record<number, { name?: string; value?: string }> = {};
    let hasDuplicates = false;
    for (let i = 0; i < rows.length; i++) {
      const name = rows[i].name.trim();
      if (!name) continue;
      const lower = name.toLowerCase();
      if (seen.has(lower)) {
        hasDuplicates = true;
        newErrors[i] = { ...newErrors[i], name: 'Duplicate header name' };
      }
      seen.add(lower);
    }
    if (hasDuplicates) {
      setErrors(prev => ({ ...prev, ...newErrors }));
    }
    return !hasDuplicates;
  };

  const handleSave = async () => {
    if (!validate() || !checkDuplicates()) return;

    // Build the map, skipping empty rows
    const headersMap: Record<string, string> = {};
    for (const row of rows) {
      const name = row.name.trim();
      if (name) {
        headersMap[name] = row.value;
      }
    }

    try {
      await updateSetting({ name: 'custom_headers', value: headersMap });
      onClose?.();
    } catch {
      // useUpdateSetting already handles error toasts via defaultErrorMessage
    }
  };

  const handleCancel = () => {
    onClose?.();
  };

  return (
    <div className="flex flex-col gap-4" data-testid="custom-scan-headers-form">
      <p className="text-chariot-text-secondary text-sm">
        Custom HTTP headers attached to all outgoing scan requests. These
        headers are sent to <span className="font-bold">every</span> target in
        the account&apos;s attack surface.
      </p>

      {/* Header rows */}
      <div className="flex flex-col gap-2">
        {/* Column labels */}
        <div className="flex items-center gap-2">
          <span className="text-chariot-text-secondary flex-1 text-xs font-medium">
            Name
          </span>
          <span className="text-chariot-text-secondary flex-1 text-xs font-medium">
            Value
          </span>
          <div className="w-9" /> {/* Spacer for delete button column */}
        </div>

        {rows.map((row, index) => (
          <div key={index} className="flex items-start gap-2">
            <div className="flex-1">
              <Input
                placeholder="X-Custom-Header"
                value={row.name}
                onChange={e => updateRow(index, 'name', e.target.value)}
                data-testid={`header-name-${index}`}
                aria-label={`Header name ${index + 1}`}
                className={errors[index]?.name ? 'border-destructive' : ''}
              />
              {errors[index]?.name && (
                <p className="text-destructive mt-1 text-xs" role="alert">
                  {errors[index].name}
                </p>
              )}
            </div>
            <div className="flex-1">
              <Input
                placeholder="Bearer token123"
                value={row.value}
                onChange={e => updateRow(index, 'value', e.target.value)}
                data-testid={`header-value-${index}`}
                aria-label={`Header value ${index + 1}`}
                className={errors[index]?.value ? 'border-destructive' : ''}
              />
              {errors[index]?.value && (
                <p className="text-destructive mt-1 text-xs" role="alert">
                  {errors[index].value}
                </p>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => removeRow(index)}
              disabled={rows.length <= 1}
              aria-label={`Remove header ${index + 1}`}
              data-testid={`remove-header-${index}`}
            >
              <TrashIcon className="size-4" />
            </Button>
          </div>
        ))}
      </div>

      {/* Add row button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={addRow}
        disabled={rows.length >= MAX_HEADERS}
        className="w-fit"
        data-testid="add-header-button"
      >
        <PlusIcon className="mr-1 size-4" />
        Add Header
      </Button>

      {/* Footer */}
      <div className="border-chariot-stroke flex items-center justify-end gap-2 border-t pt-4">
        <Button variant="ghost" onClick={handleCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          data-testid="save-headers-button"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
